import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/AuthProvider';
import { useActiveChild } from '@/contexts/ActiveChildContext';
import { isActiveStudent, isAdmin as isAdminUser, isCoordinator, isCoach as isCoachUser, isKidMember } from '@/utils/roleChecks';
import { useHasSyllabus } from '@/hooks/useMySyllabus';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from "@/components/ui/dialog";
import { Activity, BarChart3, Book, BookOpen, Calendar, ClipboardCheck, CreditCard, Download, Globe, GraduationCap, Headphones, LayoutDashboard, MessageSquare, PiggyBank, Settings, Trophy, Users } from 'lucide-react';

interface LeftSidebarProps {
    className?: string;
    onItemClick?: () => void;
}

export const LeftSidebar: React.FC<LeftSidebarProps> = ({ className, onItemClick }) => {
    const { t } = useTranslation();
    const hasSyllabus = useHasSyllabus();
    const { currentUser, hasPermission } = useAuth();
    const { activeChildId } = useActiveChild();
    const location = useLocation();
    // const { pendingRecords } = useAttendance(); // Removed for perf
    const pendingCount = 0; // pendingRecords.length;
    const { isInstallable, installApp, isIOS, isInstalled } = usePWAInstall();
    const [iosOpen, setIOSOpen] = useState(false);

    const getMenuItems = () => {
        // Base items available to everyone
        const items = [
            { icon: LayoutDashboard, label: t('nav.dashboard'), path: '/dashboard' },
        ];

        // Check if user has coach/coordinator role (primary role or in roleIds)
        const isCoach = currentUser?.role === 'coach' || currentUser?.roleIds?.includes('coach');
        const isCoord = isCoordinator(currentUser);

        // Feed
        // Hiding from Parents/Kids as requested. 
        // Using role check to preserve visibility for others (students/coaches) regardless of DB permissions.
        if (currentUser?.role !== 'parent' && !isKidMember(currentUser)) {
            items.push({ icon: MessageSquare, label: t('nav.feed'), path: '/feed' });
        }

        // Schedule - Visible to all authenticated users (parents, kids, students, coaches, coordinators)
        if (currentUser) {
            items.push({ icon: Calendar, label: t('nav.schedule'), path: '/schedule' });
        }

        // Users & Students - Coaches should see Students
        if (hasPermission('users_view', 'read') || isCoord) {
            items.push({ icon: Users, label: t('nav.users'), path: '/users' });
        } else if (hasPermission('users_manage_students', 'read') || isCoach) {
            items.push({ icon: Users, label: t('nav.students'), path: '/students' });
        }

        // Leaderboard - Show for all except pure parents without active child
        // Parent+Student viewing as self should see leaderboard (for students)
        // Parent viewing as child should see leaderboard (for kids)
        const isParent = currentUser?.role === 'parent';
        const isAlsoStudent = isActiveStudent(currentUser);
        if (!isParent || activeChildId || isAlsoStudent) {
            items.push({ icon: Trophy, label: t('nav.leaderboard'), path: '/leaderboard' });
        }

        // Attendance - Coaches should always see this
        // Admin/Coach/Coordinator View (Full Read Access)
        if (hasPermission('attendance_view', 'read') || isCoach || isCoord) {
            items.push({ icon: ClipboardCheck, label: t('nav.attendance'), path: '/attendance' });
        }
        // Student View (Self Access) - fallback if no full read permission
        // Use isActiveStudent to support multi-role users (coach+student, parent+student)
        else if (isActiveStudent(currentUser)) {
            items.push({ icon: ClipboardCheck, label: t('nav.attendance'), path: '/attendance' });
        }

        // LMS - Coaches should always see this
        // Parent+Student viewing as self should see LMS
        // Parent viewing as child should see LMS
        // Pure parent viewing as self should NOT see LMS
        if (currentUser?.role === 'parent') {
            if (activeChildId || isActiveStudent(currentUser)) {
                items.push({ icon: BookOpen, label: t('nav.lms'), path: '/lms' });
            }
        } else if (hasPermission('lms_view', 'read') || isCoach || isActiveStudent(currentUser)) {
            items.push({ icon: BookOpen, label: t('nav.lms'), path: '/lms' });
        }

        // Curriculum — directly under LMS: it is teaching content and belongs beside
        // courses. ADMINS ONLY, matching the Firestore rule exactly: every curriculum
        // collection is `allow write: if isAdmin()`, so showing this to coaches would
        // hand them a page whose every save is denied — the same UI/rule disagreement
        // that put a dead delete button in lesson Q&A and sent coordinators to the
        // wrong attendance surface. Widen it here only if the rule widens too.
        //
        // isAdmin() the helper, NOT `currentUser?.role === 'admin'` — the raw read
        // cannot see an admin granted through the Roles UI (roleIds holds doc ids).
        if (isAdminUser(currentUser)) {
            items.push({ icon: GraduationCap, label: t('nav.curriculum'), path: '/admin/curriculum' });
        }

        // My Syllabus — the member-facing, READ-ONLY view. Two gates, both required:
        //
        //  1. CONTENT. `hasSyllabus` is false until the member's own belt level has
        //     at least one technique. On 2026-09-14 that is 0 of 121 written down,
        //     so nobody sees this yet — which is the point. A member who finds an
        //     empty page once does not come back to it.
        //  2. ROLE, through the shared helpers — never a new inline
        //     `hasRoleNamed(...)` gate. Students and kids get their own belt;
        //     coaches and coordinators teach from it and get a level picker on the
        //     page itself. A coach who also trains gets both, which is the standing
        //     rule: the role a thing is used under, not the most senior one held.
        if (hasSyllabus && (isActiveStudent(currentUser) || isKidMember(currentUser)
            || isCoachUser(currentUser) || isCoord || isAdminUser(currentUser))) {
            items.push({ icon: BookOpen, label: t('nav.syllabus'), path: '/syllabus' });
        }

        // Finance - The key fix!
        if (hasPermission('finance_view', 'read') || isCoord) {
            items.push({ icon: CreditCard, label: t('nav.finance'), path: '/finance' });
        }

        // Subscriptions
        if (hasPermission('subscriptions_view', 'read')) {
            items.push({ icon: CreditCard, label: t('nav.subscriptions'), path: '/subscriptions' });
        }

        // Family Wallet - Parents viewing their own dashboard (not a child)
        if (currentUser?.role === 'parent' && !activeChildId) {
            items.push({ icon: PiggyBank, label: t('nav.family_wallet'), path: '/family-wallet' });
        }

        // Website leads (adarwishjj.com forms) - admins
        if (isAdminUser(currentUser)) {
            items.push({ icon: Globe, label: t('nav.website_leads'), path: '/admin/leads' });
        }

        // Analytics - Super Admin only
        if (currentUser?.role === 'super_admin') {
            items.push({ icon: BarChart3, label: t('nav.analytics'), path: '/analytics' });
        }

        // Performance - gated to match the /performance route (RoleGuard
        // requiredRole="coach" → only super_admin + coach can reach it).
        // Previously shown to everyone, which left a dead/blocked nav item
        // for parents, students, coordinators, and admins.
        if (currentUser && (currentUser.role === 'super_admin' || isCoach)) {
            items.push({ icon: Activity, label: t('nav.performance'), path: '/performance' });
        }

        // Settings
        if (currentUser) {
            items.push({ icon: Settings, label: t('nav.settings'), path: '/settings' });
        }

        // Knowledge Base (includes Manager tab for Super Admins)
        // Knowledge Base (includes Manager tab for Super Admins)
        items.push({ icon: Book, label: t('nav.kb'), path: '/kb' });

        // System Audit (Super Admin only)
        if (currentUser?.role === 'super_admin') {
            items.push({ icon: Activity, label: t('nav.audit_logs'), path: '/admin/audit' });
            items.push({ icon: MessageSquare, label: t('nav.support_inbox'), path: '/admin/support-inbox' });
        }

        // Contact Support (all users)
        if (currentUser) {
            items.push({ icon: Headphones, label: t('nav.contact_support'), path: '/contact-support' });
        }

        return items;
    };

    const menuItems = getMenuItems();

    // SECURITY: Don't show navigation for pending/unapproved accounts
    const isPendingApproval = currentUser?.status === 'pending';

    if (isPendingApproval) {
        return null; // Hide entire sidebar for pending accounts
    }

    return (
        <aside className={cn("overflow-y-auto pb-4", className)}>
            {/* Mobile Header with Logo (lg:hidden) */}
            <div className="lg:hidden flex items-center gap-3 px-4 py-4 pt-safe mb-2 border-b border-gray-100">
                <img src="/logo.png" alt="ADJJA Academy" className="w-8 h-8 rounded-full object-cover border border-gray-100" />
                <span className="text-lg font-bold text-black tracking-tight">ADJJA Academy</span>
            </div>

            <nav className="p-2 space-y-1">
                {menuItems.map((item) => {
                    const isActive =
                        item.path === '/feed'
                            ? location.pathname === '/feed'
                            : location.pathname === item.path || (location.pathname.startsWith(item.path) && item.path !== '/');
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={onItemClick}
                            data-tour={item.path === '/schedule' ? 'schedule-tab' : undefined}
                            className={cn(
                                "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors font-medium text-sm",
                                isActive
                                    ? "bg-primary/10 text-primary"
                                    : "text-gray-600 hover:bg-gray-100"
                            )}
                        >
                            <item.icon className={cn("h-6 w-6", isActive ? "text-primary" : "text-gray-500")} />
                            <span>{item.label}</span>
                            {item.path === '/attendance' && pendingCount > 0 && (
                                <Badge variant="destructive" className="ms-auto rounded-full px-2">
                                    {pendingCount}
                                </Badge>
                            )}
                        </Link>
                    )
                })}
            </nav>

            <div className="p-4 mt-auto space-y-4 border-t border-gray-100">
                {!isInstalled && (
                    <button
                        onClick={() => {
                            if (isInstallable) installApp();
                            else setIOSOpen(true); // Reuse the dialog for generic help too
                        }}
                        className="flex items-center gap-3 px-4 py-3 w-full rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium text-sm"
                    >
                        <Download className="h-6 w-6" />
                        <span>{t('nav.install_app')}</span>
                    </button>
                )}
                <LanguageSwitcher />

                {/* Install Instructions Modal (iOS & Generic) */}
                <Dialog open={iosOpen} onOpenChange={setIOSOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{isIOS ? 'Install on iOS' : 'Install App'}</DialogTitle>
                            <DialogDescription>
                                {isIOS
                                    ? 'To install this app on your iPhone or iPad:'
                                    : 'To install this app on your device:'}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                            {isIOS ? (
                                <>
                                    <div className="flex items-center gap-4">
                                        <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                                            <span className="font-bold">1</span>
                                        </div>
                                        <p>Tap the <span className="font-bold">Share</span> button in your browser menu bar.</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                                            <span className="font-bold">2</span>
                                        </div>
                                        <p>Scroll down and select <span className="font-bold">Add to Home Screen</span>.</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-center gap-4">
                                        <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                                            <span className="font-bold">1</span>
                                        </div>
                                        <p>Look for the <span className="font-bold">Install icon</span> (⊕ or ⬇️) in your browser's address bar.</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                                            <span className="font-bold">2</span>
                                        </div>
                                        <p>Or tap the browser menu (⋮) and select <span className="font-bold">Install App</span> or <span className="font-bold">Add to Home Screen</span>.</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </aside>
    );
};
